// «Send forespørsel» — skjemaet som velger produsenter og sender forespørselen.
//
// Brukes to steder (Utvikling | BMAx s. 5 pkt. 3):
//   - i forespørselspanelet på prosjektsiden («Send forespørsel»), og
//   - i statusdialogen når admin velger «Venter på tilbud». Der åpnes e-posten
//     også, så den kan redigeres før den går ut (`withEmailEditor`).
//
// Én komponent, så de to veiene ikke kan gli fra hverandre. Den henter
// produsentlista og de eksisterende forespørslene selv.
import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import { notifyCenter } from "utils/toast";
import {
  fetchVendorList,
  sendVendorRequest,
  listVendorQuotes,
  uploadRequestAttachment,
  setRequestAttachments,
  previewVendorRequest,
} from "api/apiService";

function VendorRequestCompose({ orderId, withEmailEditor, onSent, onCancel }) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [alreadyAsked, setAlreadyAsked] = useState(new Set());
  const [picked, setPicked] = useState([]);
  const [vendorSearch, setVendorSearch] = useState("");
  const [deadline, setDeadline] = useState("");
  const [note, setNote] = useState("");
  // Forhåndsutfylt med standardsatsen (500 kr) — «standardpris 500 kr som kan
  // endres» (s. 5 pkt. 2). Tidligere sto den bare som plassholdertekst.
  const [feeAmount, setFeeAmount] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);

  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [showSource, setShowSource] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [quotes, list, preview] = await Promise.all([
        listVendorQuotes(orderId),
        fetchVendorList().catch(() => null),
        withEmailEditor ? previewVendorRequest(orderId).catch(() => null) : null,
      ]);
      if (quotes?.ok) {
        setAlreadyAsked(
          new Set((quotes.quotes || []).filter((q) => q.state !== "cancelled").map((q) => q.vendor_id))
        );
        if (Array.isArray(quotes.attachments)) setAttachments(quotes.attachments);
        setFeeAmount((prev) => prev || String(quotes.default_fee_amount ?? 500));
        setNote((prev) => prev || quotes.vendor_note || "");
      }
      if (list?.status) setVendors(list.vendor_list || []);
      if (preview?.ok) {
        setSubject(preview.subject || "");
        setMessage(preview.message || "");
      }
    } catch (e) {
      console.error("VendorRequestCompose load:", e);
    } finally {
      setLoading(false);
    }
  }, [orderId, withEmailEditor]);

  useEffect(() => {
    load();
  }, [load]);

  const shownVendors = vendorSearch.trim()
    ? vendors.filter((v) => (v.title || "").toLowerCase().includes(vendorSearch.trim().toLowerCase()))
    : vendors;

  const toggle = (id) =>
    setPicked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  // Vedleggene lagres på ordren med én gang. Før lå de bare i skjemaet til
  // «Send», og forsvant hvis siden ble lastet på nytt.
  const saveAttachments = async (list) => {
    try {
      await setRequestAttachments(orderId, list.map((a) => a.id));
    } catch (e) {
      notifyCenter("error", "Kunne ikke lagre vedleggene på ordren.");
    }
  };

  const addFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    const added = [];
    try {
      for (const file of files) {
        try {
          const row = await uploadRequestAttachment(file);
          added.push({
            id: row.id,
            url: row.url,
            name: row.originalName || file.name,
            mime: row.mime,
            size: row.size,
          });
        } catch (e) {
          notifyCenter("error", `${file.name}: ${e.message}`);
        }
      }
    } finally {
      setUploading(false);
    }
    if (added.length) {
      const next = [...attachments, ...added];
      setAttachments(next);
      await saveAttachments(next);
    }
  };

  const removeAttachment = async (id) => {
    const next = attachments.filter((a) => a.id !== id);
    setAttachments(next);
    await saveAttachments(next);
  };

  const send = async () => {
    if (!picked.length) return;
    setBusy(true);
    try {
      const res = await sendVendorRequest(orderId, picked, {
        deadline,
        note,
        feeAmount,
        attachmentIds: attachments.map((a) => a.id),
        ...(withEmailEditor ? { subject, message } : {}),
      });
      if (!res?.ok) {
        const MSG = {
          already_taken_over: "En produsent har allerede overtatt forespørselen.",
          already_selected: "En fastpris er allerede valgt på denne ordren.",
          invalid_fee_amount: "«Ta over forespørsel»-prisen er ikke et gyldig beløp.",
          invalid_deadline: "Svarfristen er ikke en gyldig dato.",
          deadline_in_past: "Svarfristen har allerede vært.",
          vendor_ids_required: "Velg minst én produsent.",
        };
        notifyCenter("error", MSG[res?.error] || "Kunne ikke sende forespørselen.");
        return;
      }
      // Si hvem som faktisk fikk e-post. En produsent uten e-postadresse får en
      // lenke som virker, men ingen varsling.
      const failed = (res.sent || []).filter((r) => !r.email_sent);
      notifyCenter(
        failed.length ? "warning" : "success",
        failed.length
          ? `Forespørsel opprettet for ${res.sent.length} produsent(er), men e-post gikk ikke til: ${failed
              .map((f) => f.vendor_name)
              .join(", ")}. Kopier lenken og send den manuelt.`
          : `Forespørsel sendt til ${res.sent.length} produsent(er).`
      );
      setPicked([]);
      if (onSent) await onSent(res);
    } catch (e) {
      notifyCenter("error", "Kunne ikke sende forespørselen.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <SoftBox display="flex" justifyContent="center" py={2}>
        <CircularProgress size={20} />
      </SoftBox>
    );
  }

  return (
    <SoftBox>
      <SoftBox display="flex" alignItems="center" justifyContent="space-between" mt={1} mb={1} gap={2} flexWrap="wrap">
        <SoftTypography variant="button" fontWeight="bold">
          Velg produsenter{picked.length ? ` (${picked.length} valgt)` : ""}
        </SoftTypography>
        <SoftBox sx={{ width: { xs: "100%", sm: 260 } }}>
          <SoftInput
            placeholder="Søk etter produsent…"
            value={vendorSearch}
            onChange={(e) => setVendorSearch(e.target.value)}
          />
        </SoftBox>
      </SoftBox>
      {/* Taket + egen scroll: uten det dytter 90 avkryssingsbokser resten av
          siden langt ned. */}
      <SoftBox sx={{ maxHeight: 260, overflowY: "auto", pr: 1 }}>
        <Grid container spacing={0.5}>
          {shownVendors.map((v) => {
            const asked = alreadyAsked.has(v.id);
            return (
              <Grid item xs={12} sm={6} md={4} key={v.id}>
                <SoftBox display="flex" alignItems="center">
                  <Checkbox checked={picked.includes(v.id)} disabled={asked} onChange={() => toggle(v.id)} />
                  <SoftTypography
                    variant="caption"
                    sx={{ opacity: asked ? 0.5 : 1, cursor: asked ? "default" : "pointer" }}
                    onClick={() => !asked && toggle(v.id)}
                  >
                    {v.title}
                    {asked ? " (har fått den)" : ""}
                  </SoftTypography>
                </SoftBox>
              </Grid>
            );
          })}
          {!shownVendors.length && (
            <Grid item xs={12}>
              <SoftTypography variant="caption" color="text">
                Ingen produsenter matcher «{vendorSearch}».
              </SoftTypography>
            </Grid>
          )}
        </Grid>
      </SoftBox>

      <Grid container spacing={2} mt={0.5}>
        <Grid item xs={12} sm={4}>
          <SoftTypography variant="caption" fontWeight="bold" display="block" mb={0.5}>
            Svarfrist (valgfritt)
          </SoftTypography>
          <SoftInput type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <SoftTypography variant="caption" fontWeight="bold" display="block" mb={0.5}>
            «Ta over forespørsel»-pris (kr eks. mva.)
          </SoftTypography>
          <SoftInput value={feeAmount} inputMode="decimal" onChange={(e) => setFeeAmount(e.target.value)} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <SoftTypography variant="caption" fontWeight="bold" display="block" mb={0.5}>
            Notat til produsent (valgfritt)
          </SoftTypography>
          <SoftInput
            multiline
            rows={2}
            value={note}
            placeholder="F.eks. vanskelig adkomst, smal vei siste 300 m."
            onChange={(e) => setNote(e.target.value.slice(0, 1000))}
          />
        </Grid>
      </Grid>

      {/* Vedlegg — kartutsnitt, Street View-bilder, dokumentasjon vi har fått
          separat (s. 5 pkt. 2). Ligger på ordren, så alle produsentene priser
          det samme grunnlaget. */}
      <SoftBox mt={2}>
        <SoftTypography variant="caption" fontWeight="bold" display="block" mb={0.5}>
          Dokumenter og bilder (valgfritt)
        </SoftTypography>
        {attachments.length > 0 && (
          <SoftBox display="flex" flexWrap="wrap" gap={1} mb={1}>
            {attachments.map((a) => (
              <Chip
                key={a.id}
                size="small"
                variant="outlined"
                icon={<AttachFileIcon />}
                label={a.name}
                onDelete={busy ? undefined : () => removeAttachment(a.id)}
                onClick={() => window.open(a.url, "_blank", "noopener")}
              />
            ))}
          </SoftBox>
        )}
        <SoftButton component="label" size="small" variant="outlined" color="secondary" disabled={uploading || busy}>
          {uploading ? "Laster opp…" : "Legg ved fil"}
          <input
            hidden
            type="file"
            multiple
            accept="application/pdf,image/png,image/jpeg"
            onChange={(e) => {
              addFiles(e.target.files);
              // Nullstill, ellers gir «samme fil på nytt» ingen onChange.
              e.target.value = "";
            }}
          />
        </SoftButton>
        <SoftTypography variant="caption" color="text" display="block" mt={0.5}>
          PDF, PNG eller JPG, maks 10 MB per fil. Lagres på ordren med én gang, og produsentene får dem
          som lenker i e-posten og på forespørselssiden.
        </SoftTypography>
      </SoftBox>

      {withEmailEditor && (
        <SoftBox mt={2}>
          <SoftTypography variant="caption" fontWeight="bold" display="block" mb={0.5}>
            Emne
          </SoftTypography>
          <SoftInput value={subject} onChange={(e) => setSubject(e.target.value)} />
          <SoftBox display="flex" alignItems="center" justifyContent="space-between" mt={1.5} mb={0.5}>
            <SoftTypography variant="caption" fontWeight="bold">
              E-post til produsentene
            </SoftTypography>
            <SoftButton size="small" variant="text" color="secondary" onClick={() => setShowSource((v) => !v)}>
              {showSource ? (
                <>
                  <VisibilityIcon sx={{ fontSize: 15, mr: 0.5 }} /> Forhåndsvis
                </>
              ) : (
                <>
                  <EditIcon sx={{ fontSize: 15, mr: 0.5 }} /> Rediger
                </>
              )}
            </SoftButton>
          </SoftBox>
          {showSource ? (
            <SoftInput multiline rows={10} value={message} onChange={(e) => setMessage(e.target.value)} />
          ) : (
            // Vår egen mal, ikke noe brukeren har lastet opp.
            <SoftBox
              sx={{
                border: "1px solid #e6ecf3",
                borderRadius: 2,
                p: 2,
                maxHeight: 280,
                overflowY: "auto",
                backgroundColor: "#fff",
                fontSize: 14,
                "& p": { margin: "0 0 10px" },
              }}
              dangerouslySetInnerHTML={{ __html: message }}
            />
          )}
          <SoftTypography variant="caption" color="text" display="block" mt={1}>
            {"{{vendor_name}}"} og {"{{vendor_link}}"} fylles ut for hver produsent — hver får sin egen
            lenke. Notatet og vedleggene over settes inn der {"{{request_note}}"} og{" "}
            {"{{request_attachments}}"} står.
          </SoftTypography>
        </SoftBox>
      )}

      <SoftBox mt={2} display="flex" gap={1} justifyContent="flex-end" flexWrap="wrap">
        {onCancel && (
          <SoftButton size="small" variant="text" color="secondary" disabled={busy} onClick={onCancel}>
            Avbryt
          </SoftButton>
        )}
        <SoftButton color="info" variant="gradient" size="small" disabled={busy || !picked.length} onClick={send}>
          {busy ? "Sender…" : `Send forespørsel (${picked.length})`}
        </SoftButton>
      </SoftBox>
    </SoftBox>
  );
}

VendorRequestCompose.propTypes = {
  orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  withEmailEditor: PropTypes.bool,
  onSent: PropTypes.func,
  onCancel: PropTypes.func,
};

VendorRequestCompose.defaultProps = {
  withEmailEditor: false,
  onSent: null,
  onCancel: null,
};

export default VendorRequestCompose;
